import React, { createContext, useCallback, useContext, useState } from 'react';
import { FeedbackDialog, FeedbackDialogProps } from 'components/ui/feedback-dialog';

type OpenDialogOptions = Omit<FeedbackDialogProps, 'open' | 'onClose'>;

type FeedbackDialogContextType = {
  openDialog: (options: OpenDialogOptions) => void;
  closeDialog: () => void;
};

const FeedbackDialogContext = createContext<FeedbackDialogContextType | undefined>(undefined);

export const FeedbackDialogProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [stack, setStack] = useState<OpenDialogOptions[]>([]);

  const openDialog = useCallback((opts: OpenDialogOptions) => {
    setStack((prev) => [...prev, opts]);
  }, []);

  const closeDialog = useCallback(() => {
    setStack((prev) => prev.slice(0, -1));
  }, []);

  return (
    <FeedbackDialogContext.Provider value={{ openDialog, closeDialog }}>
      {children}
      {stack.map((opts, idx) => (
        <FeedbackDialog key={idx} {...opts} open={idx === stack.length - 1} onClose={closeDialog} />
      ))}
    </FeedbackDialogContext.Provider>
  );
};

export const useFeedbackDialog = (): FeedbackDialogContextType => {
  const context = useContext(FeedbackDialogContext);
  if (context === undefined) {
    throw new Error('useFeedbackDialog must be used within FeedbackDialogProvider');
  }
  return context;
};
